import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Atom, Droplet, Layers, Container } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

const FAMILIES = [
  {
    icon: Atom,
    name: "Aromatics",
    image: "/assets/chem1.webp",
    items: ["Benzene", "Toluene", "Mixed xylenes", "Paraxylene"],
    text: "Feedstocks for polymers, fibres and downstream chemical synthesis.",
  },
  {
    icon: Droplet,
    name: "Solvents",
    image: "/assets/chem2.webp",
    items: ["Methanol", "Isopropanol", "Acetone", "MEK"],
    text: "Industrial-grade solvents for coatings, adhesives, inks and process use.",
  },
  {
    icon: Layers,
    name: "Intermediates",
    image: "/assets/chem3.webp",
    items: ["MEG / DEG", "Styrene monomer"],
    text: "Building blocks for resins, polyesters and plastics manufacturing.",
  },
];

const PURITY_TABLE = [
  ["Benzene", "≥ 99.8%", "ISO tank / bulk", "Polymer & chemical feedstock"],
  ["Toluene", "≥ 99.5%", "ISO tank / drums", "Solvent, TDI feedstock"],
  ["Methanol", "≥ 99.85%", "ISO tank / bulk", "Formaldehyde, fuel blending"],
  ["Isopropanol", "≥ 99.7%", "Drums / ISO tank", "Pharma & coatings solvent"],
  ["MEG", "≥ 99.8%", "ISO tank / bulk", "Polyester fibre & resin"],
];

const LOGISTICS = [
  "ISO tank containers, ex-tank or door-to-door",
  "Flexitank and drum shipments for smaller lots",
  "Bulk parcels from regional terminals",
];

export default function Petrochemicals() {
  return (
    <div data-testid="petrochemicals-page">
      <section className="relative overflow-hidden">
        <img src="/assets/refinery.webp" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/90 to-navy" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <motion.p {...fadeUp} className="font-mono text-xs uppercase tracking-[0.35em] text-brand">Product Line 03</motion.p>
          <motion.h1 {...fadeUp} className="mt-4 text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl">Petrochemicals</motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-6 max-w-2xl text-base text-slate-300 md:text-lg">
            Aromatics, solvents and chemical intermediates moved through an ISO-tank supply chain — with purity
            verified by independent inspection on every parcel.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:px-12">
        <motion.h2 {...fadeUp} className="text-2xl font-bold text-white sm:text-3xl">Product families</motion.h2>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {FAMILIES.map((f, i) => (
            <motion.div
              key={f.name}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="overflow-hidden rounded-lg border border-navy-600 bg-navy-800 transition-colors duration-300 hover:border-brand/50"
              data-testid={`chem-family-card-${i}`}
            >
              <div className="h-44 overflow-hidden">
                <img src={f.image} alt={f.name} className="h-full w-full object-cover" />
              </div>
              <div className="p-7">
                <div className="flex items-center gap-3">
                  <f.icon className="h-6 w-6 text-brand" />
                  <h3 className="text-lg font-bold text-white">{f.name}</h3>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {f.items.map((it) => (
                    <span key={it} className="rounded-full border border-navy-600 px-3 py-1 font-mono text-xs text-brand">{it}</span>
                  ))}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-400">{f.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-y border-navy-600/60 bg-navy-800/50" data-testid="purity-section">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-12">
          <motion.h2 {...fadeUp} className="text-2xl font-bold text-white sm:text-3xl">Purity &amp; packaging guide</motion.h2>
          <div className="mt-8 overflow-x-auto rounded-lg border border-navy-600" data-testid="purity-table">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-navy-600 bg-navy-700 text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-6 py-4 font-medium">Product</th>
                  <th className="px-6 py-4 font-medium">Typical purity</th>
                  <th className="px-6 py-4 font-medium">Packaging</th>
                  <th className="px-6 py-4 font-medium">Key uses</th>
                </tr>
              </thead>
              <tbody>
                {PURITY_TABLE.map(([product, purity, pack, use]) => (
                  <tr key={product} className="border-b border-navy-600/50 bg-navy-800 last:border-0">
                    <td className="px-6 py-4 font-semibold text-white">{product}</td>
                    <td className="px-6 py-4 font-mono text-brand">{purity}</td>
                    <td className="px-6 py-4 text-slate-300">{pack}</td>
                    <td className="px-6 py-4 text-slate-400">{use}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-slate-500">
            Indicative values. Independent inspection (SGS or equivalent) is available on every shipment.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-20 md:px-12 lg:grid-cols-2">
        <motion.div {...fadeUp} className="float-slow overflow-hidden rounded-lg border border-navy-600">
          <img src="/assets/chem3.webp" alt="Petrochemical storage and handling" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div {...fadeUp}>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-brand">Supply Chain</p>
          <h2 className="mt-4 text-2xl font-bold text-white sm:text-3xl">ISO-tank logistics, handled end to end</h2>
          <ul className="mt-7 space-y-3">
            {LOGISTICS.map((l, i) => (
              <li key={l} className="flex items-center gap-3 text-sm text-slate-300" data-testid={`chem-logistics-${i}`}>
                <Container className="h-4 w-4 shrink-0 text-brand" /> {l}
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="petrochemicals-cta-quote"
              className="inline-flex items-center gap-2 rounded-md bg-brand px-7 py-3 text-sm font-semibold text-navy transition-transform duration-200 hover:scale-105 hover:shadow-[0_0_28px_rgba(0,208,132,0.4)]"
            >
              Request petrochemical pricing <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="/specs/petrochemicals-spec-sheet.pdf"
              download
              data-testid="petrochemicals-spec-download"
              className="inline-flex items-center gap-2 rounded-md border border-navy-600 px-7 py-3 text-sm font-semibold text-slate-200 transition-colors duration-200 hover:border-brand/60 hover:text-brand"
            >
              Download spec sheet (PDF)
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
